import { useState, useEffect, useCallback } from 'react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import KpiStrip from './KpiStrip';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
function fmtD(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const TOOLTIP = { contentStyle: { background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', fontSize: '12px', direction: 'rtl' } };
const PIE_COLORS = ['#059669', '#3B82F6', '#F59E0B', '#8B5CF6'];
const AGING_LABELS = [
  { key: 'current', label: 'غير مستحق', color: '#059669' },
  { key: 'b30', label: '1-30 يوم', color: '#F59E0B' },
  { key: 'b60', label: '31-60 يوم', color: '#F97316' },
  { key: 'b90', label: '61-90 يوم', color: '#EF4444' },
  { key: 'b90plus', label: '+90 يوم', color: '#DC2626' },
];

export default function CollectionsReport({ dateFrom, dateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Single SCOPED endpoint: rep sees own, supervisor sees team, manager sees all.
      // (Replaces the legacy unscoped /reports/collections + /collections/outstanding.)
      const reportRes = await api.get('/sellgo_v5_coll_report', {
        date_from: dateFrom, date_to: dateTo, limit: 200,
      });
      if (reportRes?.success) {
        const out = reportRes.data?.outstanding || [];
        setData({
          ...reportRes.data,
          outstanding: out.map((inv) => ({
            id: inv.id,
            name: inv.name,
            partner: Array.isArray(inv.partner_id) ? inv.partner_id[1] : '',
            due_date: inv.invoice_date_due
              ? new Date(inv.invoice_date_due).toLocaleDateString('ar-EG')
              : '—',
            residual: inv.amount_residual,
            days_overdue: inv.overdue_days || 0,
          })),
        });
      } else {
        setError(reportRes?.error || 'فشل تحميل تقرير التحصيلات');
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  if (error) return (
    <div className="p-6 text-center space-y-3">
      <p className="text-red-500 font-medium">{error}</p>
      <Button onClick={load} variant="outline" size="sm"><RotateCcw className="w-4 h-4 ml-1" /> إعادة المحاولة</Button>
    </div>
  );

  const kpis = data ? [
    { label: 'إجمالي المحصل', value: fmt(data.kpis.total_collected), suffix: cur(), to: '/collections' },
    { label: 'عدد الإيصالات', value: fmt(data.kpis.total_receipts), suffix: 'إيصال', to: '/collections' },
    { label: 'كفاءة التحصيل', value: String(data.kpis.efficiency), suffix: '%' },
    { label: 'إجمالي المستحقات', value: fmt(data.kpis.total_ar), suffix: cur(), to: '/collections' },
  ] : [];

  const agingPieData = AGING_LABELS.map(a => ({
    name: a.label,
    value: data?.aging?.buckets?.[a.key] || 0,
    color: a.color
  })).filter(a => a.value > 0);

  return (
    <div className="space-y-4">
      <KpiStrip kpis={kpis} loading={loading && !data} />

      {/* Collections trend */}
      <div className="bg-card border border-border rounded-2xl p-4">
        <h3 className="font-bold text-foreground mb-3">اتجاه التحصيلات</h3>
        {loading && !data ? <div className="h-48 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-secondary" /></div> :
          data?.chart?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.chart}>
                <defs>
                  <linearGradient id="collGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={v => (v / 1000) + 'k'} />
                <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`, 'تحصيل']} />
                <Area type="monotone" dataKey="total" stroke="#059669" fill="url(#collGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-muted-foreground py-10">لا توجد بيانات</p>
        }
      </div>

      {/* AR Aging + Method */}
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-card border border-border rounded-2xl p-4">
          <h3 className="font-bold text-foreground mb-3">تحليل الديون (Aging)</h3>
          {loading && !data ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> :
            agingPieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={agingPieData} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={70}>
                      {agingPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-1.5 mt-2">
                  {agingPieData.map(a => (
                    <div key={a.name} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: a.color }} />
                      <span className="text-xs text-muted-foreground">{a.name}</span>
                      <span className="text-xs font-bold ml-auto">{fmt(a.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-center text-muted-foreground py-6">لا توجد مستحقات</p>
          }
        </div>

        {data?.by_method?.length > 0 && (
          <div className="bg-card border border-border rounded-2xl p-4">
            <h3 className="font-bold text-foreground mb-3">التحصيلات حسب الوسيلة</h3>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={data.by_method} dataKey="amount" nameKey="journal" cx="50%" cy="50%" outerRadius={65}
                  label={({ journal, percent }) => `${journal} ${Math.round(percent * 100)}%`} labelLine={false}>
                  {data.by_method.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={TOOLTIP.contentStyle} formatter={v => [fmt(v) + ` ${cur()}`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Outstanding invoices table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-red-50">
          <h3 className="font-bold text-red-700">الفواتير المستحقة ({data?.outstanding?.length || 0})</h3>
        </div>
        {loading && !data ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="text-right px-3 py-2 font-semibold">الفاتورة</th>
                  <th className="text-right px-3 py-2 font-semibold">العميل</th>
                  <th className="text-right px-3 py-2 font-semibold">تاريخ الاستحقاق</th>
                  <th className="text-right px-3 py-2 font-semibold">المتبقي</th>
                  <th className="text-right px-3 py-2 font-semibold">أيام التأخر</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.outstanding || []).map(inv => {
                  const dangerClass = inv.days_overdue > 90 ? 'bg-red-50' : inv.days_overdue > 60 ? 'bg-orange-50' : inv.days_overdue > 30 ? 'bg-amber-50' : '';
                  return (
                    <tr key={inv.id} className={`${dangerClass} hover:opacity-80`}>
                      <td className="px-3 py-2 font-mono text-primary">{inv.name}</td>
                      <td className="px-3 py-2 font-medium truncate max-w-28">{inv.partner}</td>
                      <td className="px-3 py-2 text-muted-foreground">{inv.due_date}</td>
                      <td className="px-3 py-2 font-bold text-red-600">{fmtD(inv.residual)} {cur()}</td>
                      <td className="px-3 py-2">
                        {inv.days_overdue > 0
                          ? <span className={`font-bold ${inv.days_overdue > 90 ? 'text-red-700' : inv.days_overdue > 60 ? 'text-orange-600' : 'text-amber-600'}`}>{inv.days_overdue} يوم</span>
                          : <span className="text-emerald-600 font-semibold">لم يحل</span>
                        }
                      </td>
                    </tr>
                  );
                })}
                {!data?.outstanding?.length && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">لا توجد مستحقات</td></tr>}
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  );
}